import { relations } from 'drizzle-orm';
import { index, integer, jsonb, pgTable, serial, text, timestamp, uniqueIndex } from 'drizzle-orm/pg-core';
import { HashedTokenSecret, TokenPrefix, TokenSecret, TokenSeparator } from '../utils/token';
import { TypeId } from '../utils/typeid';
import { Provider } from './account';
import { application } from './application';
import { accountScope } from './scope';

export type HostedAuthSessionToken = `${TokenPrefix}${TokenSeparator}${TokenSecret}`;
export type HashedHostedAuthSessionToken = `${TokenPrefix}${TokenSeparator}${HashedTokenSecret}`;

/**
 * @note
 * Inferred from HostedAuthSessionProvidersSchema in @unipile/types
 * but can't be imported directly because of circular dependency
 */
type HostedAuthSessionProviders = '*' | '*:MESSAGING' | '*:EMAILS' | '*:CALENDAR' | '*:SOCIAL' | Provider | Provider[];

export const hostedAuthSession = pgTable(
  'hosted_auth_session',
  {
    id: serial().primaryKey(),
    prefix: text().notNull().$type<TokenPrefix>(),
    hashed_token: text().notNull().$type<HashedHostedAuthSessionToken>(),
    created_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    application_id: integer()
      .references(() => application.id, { onDelete: 'cascade' })
      .notNull(),
    account_scope_id: integer().references(() => accountScope.id, { onDelete: 'cascade' }),
    expires_on: timestamp({ withTimezone: true, mode: 'string' }).notNull(),
    // Non-null sessions have consumed their authentication rights and may only poll account_id.
    authenticated_at: timestamp({ withTimezone: true, mode: 'string' }),
    redirect_uri: text().notNull(),
    state: text(),
    providers: text().$type<HostedAuthSessionProviders>(),
    account_id: text().$type<TypeId<'account'>>(),
    config: jsonb(),
  },
  (t) => [
    uniqueIndex('hosted_auth_session_prefix_uq').on(t.prefix),
    index('hosted_auth_session_application_id_idx').on(t.application_id),
    index('hosted_auth_session_account_scope_id_idx').on(t.account_scope_id),
    index('hosted_auth_session_expires_on_idx').on(t.expires_on),
  ],
);

export const hostedAuthSessionRelations = relations(hostedAuthSession, ({ one }) => ({
  application: one(application, { fields: [hostedAuthSession.application_id], references: [application.id] }),
  accountScope: one(accountScope, { fields: [hostedAuthSession.account_scope_id], references: [accountScope.id] }),
}));
